import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import toast from 'react-hot-toast';
import './Personnel.css';
import './Dashboard.css';
import './LabTeam.css';

const LabTeam = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [employees, setEmployees] = useState([]);
  const [filteredEmployees, setFilteredEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [stationFilter, setStationFilter] = useState('الكل');
  const [roleFilter, setRoleFilter] = useState('الكل');
  const [shiftFilter, setShiftFilter] = useState('الكل');
  const [statusFilter, setStatusFilter] = useState('الكل');

  // Edit Modal States
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [editForm, setEditForm] = useState({
    fullName: '',
    phone: '',
    station: '',
    role: '',
    shift: '',
    isActive: true
  });

  // المديرين المسموح لهم بالتعديل
  const allowedManagers = [
    '01091522656', '01114140179', '01201754199',
    '01201762267', '01002212231', '01099810822'
  ];
  
  const canEdit = user && (
    user.role === 'admin' || 
    allowedManagers.includes(user.phone)
  );

  // المحطات والأدوار
  const stations = ['الكل', 'الجيزة 13', 'ميدان الجيزة 14', 'الروضة 15', 'الملك الصالح 16', 'الفسطاط 17', 'المعمل المركزي'];
  const stationsForEdit = ['الجيزة 13', 'ميدان الجيزة 14', 'الروضة 15', 'الملك الصالح 16', 'الفسطاط 17', 'المعمل المركزي'];
  const roles = ['الكل', 'مهندس', 'سوبر فايزر', 'فني', 'مساعد'];
  const rolesForEdit = [
    { label: 'مهندس', value: 'engineer' },
    { label: 'سوبر فايزر', value: 'supervisor' },
    { label: 'فني', value: 'technician' },
    { label: 'مساعد', value: 'assistant' }
  ];
  const shifts = ['الكل', 'نهار', 'ليل'];
  const shiftsForEdit = ['نهار', 'ليل'];

  // جلب الموظفين
  const fetchEmployees = async () => {
    try {
      setLoading(true);
      
      const token = localStorage.getItem('token');
      if (token) {
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      }
      
      const response = await axios.get('/api/users');
      const approved = response.data.data.users.filter(u => u.status === 'approved');
      setEmployees(approved);
      setFilteredEmployees(approved);
    } catch (error) {
      toast.error('فشل في تحميل البيانات');
      console.error('Error fetching employees:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEmployees();
  }, []);

  // تطبيق الفلاتر
  useEffect(() => {
    let filtered = [...employees];

    // بحث
    if (searchTerm) {
      filtered = filtered.filter(emp => 
        emp.fullName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        emp.phone?.includes(searchTerm)
      );
    }

    // فلتر المحطة
    if (stationFilter !== 'الكل') {
      filtered = filtered.filter(emp => emp.station === stationFilter);
    }

    // فلتر الوظيفة
    if (roleFilter !== 'الكل') {
      const roleMap = {
        'مهندس': 'engineer',
        'سوبر فايزر': 'supervisor',
        'فني': 'technician',
        'مساعد': 'assistant'
      };
      filtered = filtered.filter(emp => emp.role === roleMap[roleFilter]);
    }

    // فلتر الشيفت
    if (shiftFilter !== 'الكل') {
      filtered = filtered.filter(emp => emp.shift === shiftFilter);
    }

    // فلتر الحالة
    if (statusFilter !== 'الكل') {
      if (statusFilter === 'نشط') {
        filtered = filtered.filter(emp => emp.isActive === true);
      } else {
        filtered = filtered.filter(emp => emp.isActive === false);
      }
    }

    setFilteredEmployees(filtered);
  }, [searchTerm, stationFilter, roleFilter, shiftFilter, statusFilter, employees]);

  // فتح Modal التعديل
  const handleEdit = (emp) => {
    setSelectedEmployee(emp);
    setEditForm({
      fullName: emp.fullName || '',
      phone: emp.phone || '',
      station: emp.station || '',
      role: emp.role || '',
      shift: emp.shift || '',
      isActive: emp.isActive
    });
    setShowEditModal(true);
  };

  // إغلاق Modal
  const handleCloseModal = () => {
    setShowEditModal(false);
    setSelectedEmployee(null);
  };

  // حفظ التعديلات
  const handleSaveEdit = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.patch(`/api/users/${selectedEmployee._id}`, editForm, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('تم تحديث البيانات بنجاح');
      handleCloseModal();
      fetchEmployees();
    } catch (error) {
      toast.error(error.response?.data?.message || 'فشل التحديث');
    }
  };

  // ترجمة الأدوار
  const getRoleLabel = (role) => {
    const roles = {
      'technician': 'فني',
      'engineer': 'مهندس',
      'supervisor': 'سوبر فايزر',
      'senior': 'سنيور',
      'assistant': 'مساعد',
      'admin': 'الإدارة'
    };
    return roles[role] || role;
  };

  if (loading) {
    return (
      <div className="personnel-container">
        <div className="back-button-container">
          <button 
            onClick={() => navigate('/personnel')} 
            className="back-button"
          >
            <span className="back-button-icon">←</span>
            <span>العودة للأفراد</span>
          </button>
        </div>
        
        {/* اللوجو منفصل */}
        <div style={{
          background: 'white',
          padding: '20px',
          marginBottom: '0',
          display: 'flex',
          justifyContent: 'center',
          borderBottom: '2px solid #e0e0e0'
        }}>
          <img 
            src="/logo.jpg" 
            alt="Logo" 
            style={{
              height: '60px',
              width: 'auto',
              objectFit: 'contain'
            }}
          />
        </div>

        {/* البنر البنفسجي */}
        <div className="lab-team-banner">
          <div className="lab-team-banner-content">
            <h1 className="lab-team-title">أفراد المعامل</h1>
            <p className="lab-team-description">
              عرض وإدارة جميع موظفي المعامل والمحطات
            </p>
          </div>
        </div>
        
        <div style={{ textAlign: 'center', padding: '50px', color: '#7f8c8d' }}>
          <div style={{ fontSize: '3rem', marginBottom: '20px' }}>⏳</div>
          <p>جاري التحميل...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="personnel-container">
      <div className="back-button-container">
        <button 
          onClick={() => navigate('/personnel')} 
          className="back-button"
        >
          <span className="back-button-icon">←</span>
          <span>العودة للأفراد</span>
        </button>
      </div>

      {/* اللوجو منفصل */}
      <div style={{
        background: 'white',
        padding: '20px',
        marginBottom: '0',
        display: 'flex',
        justifyContent: 'center',
        borderBottom: '2px solid #e0e0e0'
      }}>
        <img 
          src="/logo.jpg" 
          alt="Logo" 
          style={{
            height: '60px',
            width: 'auto',
            objectFit: 'contain'
          }}
        />
      </div>

      {/* البنر البنفسجي */}
      <div className="lab-team-banner">
        <div className="lab-team-banner-content">
          <h1 className="lab-team-title">أفراد المعامل</h1>
          <p className="lab-team-description">
            عرض وإدارة جميع موظفي المعامل والمحطات
          </p>
        </div>
      </div>
	  {/* الفلاتر */}
      <div style={{
        background: 'white',
        padding: '25px',
        borderRadius: '12px',
        boxShadow: '0 2px 15px rgba(0,0,0,0.08)',
        marginBottom: '25px'
      }}>
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '15px',
          marginBottom: '15px'
        }}>
          {/* بحث */}
          <div>
            <label style={{ 
              display: 'block', 
              marginBottom: '8px', 
              fontWeight: '600', 
              fontSize: '0.95rem', 
              color: '#2c3e50'
            }}>
              🔍 البحث
            </label>
            <input
              type="text"
              placeholder="الاسم أو الموبايل"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                width: '100%',
                padding: '12px 15px',
                border: '2px solid #e0e0e0',
                borderRadius: '8px',
                fontSize: '1rem',
                outline: 'none',
                transition: 'border 0.3s'
              }}
              onFocus={(e) => e.target.style.borderColor = '#3498db'}
              onBlur={(e) => e.target.style.borderColor = '#e0e0e0'}
            />
          </div>

          {/* فلتر المحطة */}
          <div>
            <label style={{ 
              display: 'block', 
              marginBottom: '8px', 
              fontWeight: '600', 
              fontSize: '0.95rem', 
              color: '#2c3e50'
            }}>
              📍 المحطة
            </label>
            <select
              value={stationFilter}
              onChange={(e) => setStationFilter(e.target.value)}
              style={{
                width: '100%',
                padding: '12px 15px',
                border: '2px solid #e0e0e0',
                borderRadius: '8px',
                fontSize: '1rem',
                outline: 'none',
                cursor: 'pointer'
              }}
            >
              {stations.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>

          {/* فلتر الوظيفة */}
          <div>
            <label style={{ 
              display: 'block', 
              marginBottom: '8px', 
              fontWeight: '600', 
              fontSize: '0.95rem', 
              color: '#2c3e50'
            }}>
              💼 الوظيفة
            </label>
            <select
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value)}
              style={{
                width: '100%',
                padding: '12px 15px',
                border: '2px solid #e0e0e0',
                borderRadius: '8px',
                fontSize: '1rem',
                outline: 'none',
                cursor: 'pointer'
              }}
            >
              {roles.map(r => <option key={r} value={r}>{r}</option>)}
            </select>
          </div>

          {/* فلتر الشيفت */}
          <div>
            <label style={{ 
              display: 'block', 
              marginBottom: '8px', 
              fontWeight: '600', 
              fontSize: '0.95rem', 
              color: '#2c3e50'
            }}>
              🕐 الشيفت
            </label>
            <select
              value={shiftFilter}
              onChange={(e) => setShiftFilter(e.target.value)}
              style={{
                width: '100%',
                padding: '12px 15px',
                border: '2px solid #e0e0e0',
                borderRadius: '8px',
                fontSize: '1rem',
                outline: 'none',
                cursor: 'pointer'
              }}
            >
              {shifts.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>

          {/* فلتر الحالة */}
          <div>
            <label style={{ 
              display: 'block', 
              marginBottom: '8px', 
              fontWeight: '600', 
              fontSize: '0.95rem', 
              color: '#2c3e50'
            }}>
              ⚡ الحالة
            </label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              style={{
                width: '100%',
                padding: '12px 15px',
                border: '2px solid #e0e0e0',
                borderRadius: '8px',
                fontSize: '1rem',
                outline: 'none',
                cursor: 'pointer'
              }}
            >
              <option value="الكل">الكل</option>
              <option value="نشط">نشط 🟢</option>
              <option value="غير نشط">غير نشط 🔴</option>
            </select>
          </div>
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '15px' }}>
          <span style={{ color: '#7f8c8d', fontSize: '0.95rem' }}>
            عرض {filteredEmployees.length} من {employees.length} موظف
          </span>
          <button
            onClick={() => {
              setSearchTerm('');
              setStationFilter('الكل');
              setRoleFilter('الكل');
              setShiftFilter('الكل');
              setStatusFilter('الكل');
            }}
            style={{
              background: '#ecf0f1',
              border: 'none',
              padding: '8px 20px',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '0.9rem',
              color: '#2c3e50',
              fontWeight: '500'
            }}
          >
            ↻ إعادة تعيين
          </button>
        </div>
      </div>

	  {/* الجدول */}
      <div className="project-info">
        <div style={{ overflowX: 'auto' }}>
          <table style={{
            width: '100%',
            borderCollapse: 'collapse',
            background: 'white',
            borderRadius: '8px',
            overflow: 'hidden'
          }}>
            <thead>
              <tr style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', borderBottom: '2px solid #667eea' }}>
                <th style={{ padding: '15px', textAlign: 'right', color: 'white', fontWeight: '600' }}>الاسم</th>
                <th style={{ padding: '15px', textAlign: 'right', color: 'white', fontWeight: '600' }}>الموبايل</th>
                <th style={{ padding: '15px', textAlign: 'right', color: 'white', fontWeight: '600' }}>المحطة</th>
                <th style={{ padding: '15px', textAlign: 'right', color: 'white', fontWeight: '600' }}>الوظيفة</th>
                <th style={{ padding: '15px', textAlign: 'right', color: 'white', fontWeight: '600' }}>الشيفت</th>
                <th style={{ padding: '15px', textAlign: 'center', color: 'white', fontWeight: '600' }}>الحالة</th>
                {canEdit && (
                  <th style={{ padding: '15px', textAlign: 'center', color: 'white', fontWeight: '600' }}>الإجراءات</th>
                )}
              </tr>
            </thead>
            <tbody>
              {filteredEmployees.map((emp, index) => (
                <tr 
                  key={emp._id}
                  style={{ 
                    borderBottom: '1px solid #f0f0f0',
                    background: index % 2 === 0 ? 'white' : '#fafafa'
                  }}
                >
                  <td style={{ padding: '15px', color: '#2c3e50', fontWeight: '500' }}>
                    {emp.fullName}
                  </td>
                  <td style={{ padding: '15px', color: '#7f8c8d', direction: 'ltr', textAlign: 'right' }}>
                    {emp.phone || '-'}
                  </td>
                  <td style={{ padding: '15px', color: '#7f8c8d' }}>
                    {emp.station || '-'}
                  </td>
                  <td style={{ padding: '15px' }}>
                    <span style={{
                      background: '#e3f2fd',
                      color: '#1976d2',
                      padding: '5px 12px',
                      borderRadius: '20px',
                      fontSize: '0.85rem',
                      fontWeight: '500'
                    }}>
                      {getRoleLabel(emp.role)}
                    </span>
                  </td>
                  <td style={{ padding: '15px', color: '#7f8c8d' }}>
                    {emp.shift || '-'}
                  </td>
                  <td style={{ padding: '15px', textAlign: 'center' }}>
                    <span style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '5px',
                      padding: '5px 12px',
                      borderRadius: '20px',
                      fontSize: '0.85rem',
                      fontWeight: '500',
                      background: emp.isActive ? '#d4edda' : '#f8d7da',
                      color: emp.isActive ? '#155724' : '#721c24'
                    }}>
                      <span style={{ fontSize: '1rem' }}>{emp.isActive ? '🟢' : '🔴'}</span>
                      {emp.isActive ? 'نشط' : 'غير نشط'}
                    </span>
                  </td>
                  {canEdit && (
                    <td style={{ padding: '15px', textAlign: 'center' }}>
                      <button
                        onClick={() => handleEdit(emp)}
                        style={{
                          background: '#3498db',
                          color: 'white',
                          border: 'none',
                          padding: '6px 15px',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          fontSize: '0.85rem',
                          marginLeft: '5px'
                        }}
                      >
                        ✏️ تعديل
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>

          {filteredEmployees.length === 0 && (
            <div style={{ textAlign: 'center', padding: '50px', color: '#7f8c8d' }}>
              <div style={{ fontSize: '3rem', marginBottom: '20px' }}>🔍</div>
              <p>لا توجد نتائج</p>
            </div>
          )}
        </div>
      </div>

      {/* Edit Modal */}
      {showEditModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.6)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }} onClick={handleCloseModal}>
          <div style={{
            background: 'white',
            borderRadius: '15px',
            padding: '30px',
            width: '90%',
            maxWidth: '600px',
            maxHeight: '90vh',
            overflowY: 'auto',
            boxShadow: '0 10px 40px rgba(0,0,0,0.3)'
          }} onClick={(e) => e.stopPropagation()}>
            <h2 style={{ marginBottom: '25px', color: '#2c3e50', fontSize: '1.8rem', fontWeight: '700' }}>
              ✏️ تعديل بيانات الموظف
            </h2>

            {/* الاسم */}
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '700', fontSize: '1.1rem', color: '#2c3e50' }}>
                👤 الاسم الكامل
              </label>
              <input
                type="text"
                value={editForm.fullName}
                onChange={(e) => setEditForm({...editForm, fullName: e.target.value})}
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '2px solid #e0e0e0',
                  borderRadius: '8px',
                  fontSize: '1rem',
                  outline: 'none'
                }}
              />
            </div>

            {/* الموبايل */}
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '700', fontSize: '1.1rem', color: '#2c3e50' }}>
                📱 رقم الموبايل
              </label>
              <input
                type="text"
                value={editForm.phone}
                onChange={(e) => setEditForm({...editForm, phone: e.target.value})}
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '2px solid #e0e0e0',
                  borderRadius: '8px',
                  fontSize: '1rem',
                  outline: 'none',
                  direction: 'ltr'
                }}
              />
            </div>

            {/* المحطة */}
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '700', fontSize: '1.1rem', color: '#2c3e50' }}>
                📍 المحطة
              </label>
              <select
                value={editForm.station}
                onChange={(e) => setEditForm({...editForm, station: e.target.value})}
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '2px solid #e0e0e0',
                  borderRadius: '8px',
                  fontSize: '1rem',
                  outline: 'none',
                  cursor: 'pointer'
                }}
              >
                <option value="">اختر المحطة</option>
                {stationsForEdit.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>

            {/* الوظيفة */}
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '700', fontSize: '1.1rem', color: '#2c3e50' }}>
                💼 الوظيفة
              </label>
              <select
                value={editForm.role}
                onChange={(e) => setEditForm({...editForm, role: e.target.value})}
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '2px solid #e0e0e0',
                  borderRadius: '8px',
                  fontSize: '1rem',
                  outline: 'none',
                  cursor: 'pointer'
                }}
              >
                <option value="">اختر الوظيفة</option>
                {rolesForEdit.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
              </select>
            </div>

            {/* الشيفت */}
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '700', fontSize: '1.1rem', color: '#2c3e50' }}>
                🕐 الشيفت
              </label>
              <select
                value={editForm.shift}
                onChange={(e) => setEditForm({...editForm, shift: e.target.value})}
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '2px solid #e0e0e0',
                  borderRadius: '8px',
                  fontSize: '1rem',
                  outline: 'none',
                  cursor: 'pointer'
                }}
              >
                <option value="">اختر الشيفت</option>
                {shiftsForEdit.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>

            {/* الحالة */}
            <div style={{ marginBottom: '25px' }}>
              <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', userSelect: 'none' }}>
                <input
                  type="checkbox"
                  checked={editForm.isActive}
                  onChange={(e) => setEditForm({...editForm, isActive: e.target.checked})}
                  style={{ marginLeft: '10px', width: '18px', height: '18px', cursor: 'pointer' }}
                />
                <span style={{ fontWeight: '700', fontSize: '1.1rem', color: '#2c3e50' }}>✅ الحساب نشط</span>
              </label>
            </div>

            {/* الأزرار */}
            <div style={{ display: 'flex', gap: '15px', justifyContent: 'flex-end' }}>
              <button
                onClick={handleCloseModal}
                style={{
                  background: '#95a5a6',
                  color: 'white',
                  border: 'none',
                  padding: '12px 30px',
                  borderRadius: '8px',
                  fontSize: '1rem',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                إلغاء
              </button>
              <button
                onClick={handleSaveEdit}
                style={{
                  background: 'linear-gradient(135deg, #27ae60 0%, #229954 100%)',
                  color: 'white',
                  border: 'none',
                  padding: '12px 30px',
                  borderRadius: '8px',
                  fontSize: '1rem',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                💾 حفظ التغييرات
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LabTeam;